// ./src/utils/api/EventSetupService.ts
//
// The EVENT SETUP CATALOG (ROADMAP A2b #62) — every event type with its search words and the features it offers, plus
// the seeded setup rules. ONE read: the catalog is small and the wizard's type search must answer as the host types,
// so it is loaded once (EventSetupCatalogProvider) and searched locally (`utils/eventTypeSearch`).
//
// ⓘ The FEATURES are not here — they are generated into `config/eventSetup.generated.ts` (the menu needs them at build
// time). The rules ARE here, and are evaluated only by the generated `evaluateSetupRules` — never re-implemented.
// ⓘ Public on the server (seeded vocabulary), so it also works in a password-crew session.
import { DocumentNode, gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import type { EventSetupRule } from '@/config/eventSetup.generated';

import { ApiService } from './ApiService';
import { refusedGifts, refusedIdeas } from './catalogFields';

/**
 * ⚠ `ideas` (#62 P6(h)) and a type's `gifts` (#140) are NEW fields: a gateway that has not been recomposed REFUSES the
 * whole query over one, which would leave the wizard with no catalog at all. So the read retries WITHOUT each field the
 * gateway named (`refusedIdeas` / `refusedGifts`) — an old gateway costs the type's own starters or its gift question,
 * never the wizard (the site's `refusedSlotPeople` rule).
 */
const IDEAS_FIELD = 'ideas';
const GIFTS_FIELD = 'gifts { ideas }';

interface CatalogFields {
  ideas: boolean;
  gifts: boolean;
}

const catalogQuery = ({ ideas: withIdeas, gifts: withGifts }: CatalogFields) => gql(`
  query EventSetupCatalog {
    eventSetupCatalog {
      code
      errors {
        name
        errors
        warnings
      }
      data {
        types {
          id
          parentId
          code
          name
          categoryGroup
          emoji
          description
          allowsRecurring
          defaultEventMode
          ${withGifts ? GIFTS_FIELD : ''}
          orderNo
          synonyms
          offers {
            featureCode
            strength
            reason
            options {
              gamePresets
              voucherIdeas
              questionIdeas
              ${withIdeas ? IDEAS_FIELD : ''}
            }
          }
        }
        rules {
          code
          description
          condition {
            eventModes
            categoryCodes
            categoryGroups
            featuresOn
            spaceIds
          }
          effect {
            enable
            suggest
            disable {
              feature
              reason
              unlock {
                eventMode
              }
            }
          }
        }
      }
    }
  }
`);

// One parsed document per field set (at most four), built on first use.
const QUERIES = new Map<string, DocumentNode>();

const queryFor = (fields: CatalogFields): DocumentNode => {
  const key = `${fields.ideas}:${fields.gifts}`;
  const known = QUERIES.get(key);

  if (known) {
    return known;
  }

  const query = catalogQuery(fields);

  QUERIES.set(key, query);

  return query;
};

// The refusals it retries on live in a pure module, so the guard can run them.
export { refusedGifts, refusedIdeas };

export interface EventSetupOffer {
  /** An EVENT feature code (config/eventSetup.generated EVENT_FEATURE_CODES). */
  featureCode: string;
  /** 'suggested' (pre-ticked, shown first) | 'offered'. */
  strength: string;
  reason?: string | null;
  options?: {
    gamePresets?: string[] | null;
    voucherIdeas?: string[] | null;
    questionIdeas?: string[] | null;
    /** timeline / tasks / budget / signup_list / polls — the type's own ideas for that step, as names. */
    ideas?: string[] | null;
  } | null;
}

/** #140 — what a type does about gifts. */
export interface EventSetupGifts {
  /** The gifts a gift list for this type usually starts with ("Diapers", "Books"). */
  ideas?: string[] | null;
}

export interface EventSetupType {
  id: string;
  /** The parent type (Holiday Party for Christmas); null = top-level. */
  parentId: string | null;
  code: string;
  name: string;
  categoryGroup: string;
  emoji: string | null;
  description: string | null;
  allowsRecurring: boolean;
  /** The mode the wizard starts on for this type; null = the account's default. */
  defaultEventMode: string | null;
  /**
   * #140 — set when guests usually bring a gift to this type (a baby shower, a wedding, a child's birthday): the wizard
   * then offers a gift registry or a gift list, starting from these ideas. Null (or absent, from an older gateway) = not.
   */
  gifts?: EventSetupGifts | null;
  orderNo: number;
  /** Lowercase search words that lead here. */
  synonyms: string[];
  offers: EventSetupOffer[];
}

export interface EventSetupRuleRow extends EventSetupRule {
  description?: string | null;
}

export interface EventSetupCatalog {
  types: EventSetupType[];
  rules: EventSetupRuleRow[];
}

export class EventSetupService {
  static async getCatalog(): Promise<OpResult> {
    const result = await ApiService.executeQuery(queryFor({ ideas: true, gifts: true }), {});

    // ⚠ A stale gateway names EVERY field it does not know in one answer — so one retry, without each one it named.
    if (refusedIdeas(result) || refusedGifts(result)) {
      return ApiService.executeQuery(queryFor({ ideas: !refusedIdeas(result), gifts: !refusedGifts(result) }), {});
    }

    return result;
  }
}
